import React, { useEffect, useState } from 'react';
import { cn } from '../../utils/cn';

export interface TutorialSpotlightProps {
  targetIds: string[];
  className?: string;
  padding?: number;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Creates a spotlight effect by highlighting specific elements
 * Uses SVG mask to create cutouts in the dimmed overlay
 */
export const TutorialSpotlight: React.FC<TutorialSpotlightProps> = ({
  targetIds,
  className,
  padding = 8
}) => {
  const [spotlightRects, setSpotlightRects] = useState<SpotlightRect[]>([]);

  useEffect(() => {
    const updateSpotlights = () => {
      const rects: SpotlightRect[] = [];

      targetIds.forEach(targetId => {
        const element = document.querySelector(`[data-tutorial-id="${targetId}"]`);

        if (element) {
          const rect = element.getBoundingClientRect();

          rects.push({
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2
          });
        }
      });

      setSpotlightRects(rects);
    };

    // Initial update
    updateSpotlights();

    // Update on window resize or scroll
    window.addEventListener('resize', updateSpotlights);
    window.addEventListener('scroll', updateSpotlights, true);

    return () => {
      window.removeEventListener('resize', updateSpotlights);
      window.removeEventListener('scroll', updateSpotlights, true);
    };
  }, [targetIds, padding]);

  if (spotlightRects.length === 0) {
    return null;
  }

  return (
    <div className={cn('fixed inset-0 pointer-events-none z-[9998]', className)}>
      <svg
        width="100%"
        height="100%"
        className="absolute inset-0"
      >
        <defs>
          <mask id="tutorial-spotlight-mask">
            {/* White rectangle covering entire screen */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />

            {/* Black rectangles for cutouts (highlighted areas) */}
            {spotlightRects.map((rect, index) => (
              <rect
                key={index}
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                fill="black"
                rx="8"
              />
            ))}
          </mask>
        </defs>

        {/* Dimmed overlay with mask applied */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#tutorial-spotlight-mask)"
        />
      </svg>

      {/* Highlight borders around spotlit areas */}
      {spotlightRects.map((rect, index) => (
        <div
          key={index}
          className="absolute border-2 border-blue-500 rounded-lg pointer-events-none"
          style={{
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`
          }}
        />
      ))}
    </div>
  );
};

export default TutorialSpotlight;
